/** Seat utilisation per SKU — assigned versus unused, worst first.
 *
 *  One `SegmentBar` per SKU: the composition is the point, so a proportional
 *  split reads better than two numbers side by side. Sorted by unused seats
 *  descending, because the biggest bar of wasted spend is the reason anyone
 *  opened this page.
 *
 *  The bar is measured against **purchased** seats. Expiring capacity is a
 *  badge, never a segment: a subscription in its grace period has ended, so
 *  drawing it as part of the pool is the error this board corrects. */

import { Badge, Card, SegmentBar, Typography } from "@norican/react";

import type { LicenseSkuRow } from "@/lib/api";

import { formatPct, formatSeats, SEAT_SEGMENT_COLOR } from "./licence-format";

interface SeatUtilisationProps {
  skus: LicenseSkuRow[];
  /** How many SKUs to draw. The long tail belongs in the table below. */
  limit?: number;
}

export function SeatUtilisation({ skus, limit = 12 }: SeatUtilisationProps) {
  const rows = skus.filter((sku) => sku.counted && sku.purchased > 0).slice(0, limit);

  if (rows.length === 0) {
    return null;
  }

  return (
    <Card variant="outline">
      <Card.Header>
        <div className="flex items-baseline justify-between gap-4">
          <Typography variant="heading-4" as="h2">
            Seat utilisation by SKU
          </Typography>
          <Typography variant="body-sm" tone="muted">
            Most unused seats first
          </Typography>
        </div>
      </Card.Header>
      <Card.Body>
        <ul className="flex flex-col gap-5">
          {rows.map((sku) => (
            <li key={sku.sku_id || sku.sku_part_number} className="flex flex-col gap-2">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-fg text-sm font-medium">{sku.name}</span>
                  {sku.grace > 0 && (
                    <Badge tone="warning" variant="subtle" size="xs">
                      {formatSeats(sku.grace)} expiring
                    </Badge>
                  )}
                  {sku.over_assigned > 0 && (
                    <Badge tone="danger" variant="subtle" size="xs">
                      {formatSeats(sku.over_assigned)} beyond paid seats
                    </Badge>
                  )}
                </div>
                <span className="text-fg-muted text-xs tabular-nums">
                  {formatSeats(sku.consumed)} / {formatSeats(sku.purchased)} ·{" "}
                  {formatPct(sku.utilisation_pct)}
                </span>
              </div>
              {/* The bar's total is whichever is larger, so an over-assignment
                  extends the bar past purchased capacity in danger tone rather
                  than being clipped out of sight. */}
              <SegmentBar
                size="md"
                total={Math.max(sku.purchased, sku.consumed)}
                segments={[
                  {
                    value: Math.min(sku.consumed, sku.purchased),
                    label: "Assigned",
                    color: SEAT_SEGMENT_COLOR.assigned,
                  },
                  {
                    value: sku.unused,
                    label: "Unused",
                    color: SEAT_SEGMENT_COLOR.unused,
                  },
                  {
                    value: sku.over_assigned,
                    label: "Beyond paid seats",
                    color: SEAT_SEGMENT_COLOR.overAssigned,
                  },
                ]}
                aria-label={`${sku.name}: ${sku.consumed} assigned against ${sku.purchased} paid seats`}
              />
            </li>
          ))}
        </ul>
      </Card.Body>
    </Card>
  );
}
